import { Box, Center, Flex, Spinner, Text } from "@chakra-ui/react";
import SanctionBanner from "@components/SanctionBanner";
import { chainDefiSectionMapping } from "@constants/common";
import usePageData from "@hooks/usePageData";
import useSanction from "@hooks/useSanction";
import { useSearchParams } from "next/navigation";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { DEFI_SELECT_CHAIN } from "../../../constants/analytics";
import { trackEvent } from "../../../utils";
import CategoryDropdown from "./CategoryDropdown";
import DefiList from "./DefiItem";
import FeaturedDefi from "./FeaturedDefi";
import { DeFiRow, PageData } from "./type";

const Header = () => {
  //test comment
  return (
    <Flex
      justifyContent="space-between"
      alignItems="flex-end"
      p={{ base: "0px 12px", lg: 0 }}
    >
      <Flex flexDir="column" gap="4px">
        <Text
          fontSize={{ base: "24px", md: "32px" }}
          lineHeight={{ base: "28px", md: "38px" }}
          fontWeight="600"
          color="textPrimary"
        >
          DeFi
        </Text>
        <Text fontSize={{ base: "12px", md: "14px" }} color="textPrimary">
          Use rsETH and explore rewards using DeFi opportunities
        </Text>
      </Flex>
    </Flex>
  );
};

const Defi = () => {
  const pageData = usePageData() as PageData | undefined;
  const searchParams = useSearchParams();

  const defaultChainFilter = searchParams.get("chain");
  const defaultAssetFilter = searchParams.get("asset");
  const { isSanctioned } = useSanction();

  const ActivityData = useMemo(() => {
    const activity = new Set<string>();
    pageData?.pools.data.forEach((row) => {
      if (row.Activity != "") activity.add(row.Activity);
    });
    return Array.from(activity);
  }, [pageData?.pools.data]);

  const AssetData = useMemo(() => {
    const assets = new Set<string>();
    pageData?.pools.data.forEach((row) => {
      if (row.Activity != "") assets.add(row["Token 1 Icon"]);
    });
    return Array.from(assets);
  }, [pageData?.pools.data]);

  const sections = useMemo(() => {
    const sections: { [key: string]: DeFiRow[] } = {};
    pageData?.pools.data.forEach((row) => {
      if (row.Section) {
        sections[row.Section] = sections[row.Section] || [];
        sections[row.Section].push(row);
      }
    });
    return sections;
  }, [pageData?.pools.data]);

  const [selectedTag, setSelectedTag] = useState<string[]>([
    "All",
    ...Object.keys(sections),
  ]);

  const [selectedActivity, setSelectedActivity] = useState<string[]>([
    "All",
    ...Object.values(ActivityData),
  ]);

  const [selectedAsset, setSelectedAsset] = useState<string[]>([
    "All",
    ...Object.values(AssetData),
  ]);

  useEffect(() => {
    if (
      defaultChainFilter &&
      chainDefiSectionMapping[Number(defaultChainFilter)]
    ) {
      setSelectedTag([chainDefiSectionMapping[Number(defaultChainFilter)]]);
    }
  }, [defaultChainFilter]);

  useEffect(() => {
    if (
      defaultAssetFilter &&
      Object.values(AssetData).includes(defaultAssetFilter)
    ) {
      setSelectedAsset([defaultAssetFilter]);
    }
  }, [defaultAssetFilter]);

  const filteredSections = useMemo(() => {
    if (selectedTag[0] === "All") {
      return sections;
    }

    let filtered = {};
    selectedTag.forEach((tag: string) => {
      filtered = { ...filtered, [tag]: sections[tag] };
    });

    return filtered;

    // return { [selectedTag]: sections[selectedTag] };
  }, [sections, selectedTag]);

  const tags = useMemo(() => {
    return ["All", ...Object.keys(sections)];
  }, [sections]);

  const activities = useMemo(() => {
    return ["All", ...Object.values(ActivityData)];
  }, [ActivityData]);

  const assets = useMemo(() => {
    return ["All", ...Object.values(AssetData)];
  }, [AssetData]);

  const filterChain = (option: string) => {
    let updatedSelectedTag: string[] = [];
    if (selectedTag.includes("All")) {
      if (option === "All") {
        updatedSelectedTag = [];
      } else {
        const updatedList = selectedTag.filter((item) => item !== option);
        updatedSelectedTag = updatedList.slice(1);
      }
    } else if (!selectedTag.includes("All") && option === "All") {
      updatedSelectedTag = ["All", ...Object.keys(sections)];
    } else {
      if (selectedTag.includes(option)) {
        const updatedList = selectedTag.filter((item) => item !== option);
        updatedSelectedTag = updatedList;
      } else {
        if (selectedTag.length + 1 === Object.keys(sections).length) {
          updatedSelectedTag = ["All", ...selectedTag, option];
        } else {
          updatedSelectedTag = [...selectedTag, option];
        }
      }
    }

    setSelectedTag(updatedSelectedTag);
    trackEvent(DEFI_SELECT_CHAIN, {
      selected_chain: updatedSelectedTag.join(","),
    });
  };

  const filterActivity = (option: string) => {
    if (selectedActivity.includes("All")) {
      if (option === "All") {
        setSelectedActivity([]);
      } else {
        const updatedList = selectedActivity.filter((item) => item !== option);
        setSelectedActivity(updatedList.slice(1));
      }
    } else if (!selectedActivity.includes("All") && option === "All") {
      setSelectedActivity(["All", ...Object.values(ActivityData)]);
    } else {
      if (selectedActivity.includes(option)) {
        const updatedList = selectedActivity.filter((item) => item !== option);
        setSelectedActivity(updatedList);
      } else {
        if (
          selectedActivity.length + 1 ===
          Object.values(ActivityData).length
        ) {
          setSelectedActivity(["All", ...selectedActivity, option]);
        } else {
          setSelectedActivity([...selectedActivity, option]);
        }
      }
    }
    // if (selectedActivity.includes(option)) {
    //   const updatedList = selectedActivity.filter((item) => item !== option);
    //   setSelectedActivity(updatedList);
    // } else {
    //   setSelectedActivity([...selectedActivity, option]);
    // }
  };

  const filterAsset = (option: string) => {
    if (selectedAsset.includes("All")) {
      if (option === "All") {
        setSelectedAsset([]);
      } else {
        const updatedList = selectedAsset.filter((item) => item !== option);
        setSelectedAsset(updatedList.slice(1));
      }
    } else if (!selectedAsset.includes("All") && option === "All") {
      setSelectedAsset(["All", ...Object.values(AssetData)]);
    } else {
      if (selectedAsset.includes(option)) {
        const updatedList = selectedAsset.filter((item) => item !== option);
        setSelectedAsset(updatedList);
      } else {
        if (selectedAsset.length + 1 === Object.values(AssetData).length) {
          setSelectedAsset(["All", ...selectedAsset, option]);
        } else {
          setSelectedAsset([...selectedAsset, option]);
        }
      }
    }
  };

  const getFilteredRows = useCallback(
    (rows: DeFiRow[], featured = false) => {
      return rows.filter((row) => {
        if (!featured) {
          return (
            selectedActivity.includes(row.Activity) &&
            selectedAsset.includes(row["Token 1 Icon"])
          );
        } else {
          return (
            selectedActivity.includes(row.Activity) &&
            selectedAsset.includes(row["Token 1 Icon"]) &&
            row.Featured.toLowerCase() === "yes"
          );
        }
      });
    },
    [selectedActivity, selectedAsset]
  );

  const featuredDefi = useMemo(() => {
    if (selectedActivity.length > 0) {
      const filtered: any[] = [];
      Object.keys(filteredSections).map((section) =>
        filtered.push(...getFilteredRows(sections[section], true))
      );
      return filtered;
    } else {
      return [];
    }
  }, [filteredSections, getFilteredRows, sections, selectedActivity.length]);

  const renderMissingSelectionMessage = () => {
    let message = "";
    const sectionLength = Object.keys(filteredSections).length;
    if (selectedActivity.length === 0 && sectionLength === 0) {
      message =
        "Please select atleast 1 Chain, Activity & Asset to see DeFi activities";
    } else if (selectedActivity.length === 0 && sectionLength !== 0) {
      message = "Please select atleast 1 Activity to see DeFi activities";
    } else if (selectedActivity.length !== 0 && sectionLength === 0) {
      message = "Please select atleast 1 Chain to see DeFi activities";
    } else if (
      selectedActivity.length !== 0 &&
      sectionLength !== 0 &&
      selectedAsset.length === 0
    ) {
      message = "Please select atleast 1 Asset to see DeFi activities";
    } else {
      return;
    }
    return (
      <Flex
        overflowX="auto"
        maxW="100%"
        mt={{ base: 8, md: 0 }}
        flex={1}
        justifyContent={"center"}
        alignItems={"center"}
        height={"50vh"}
      >
        {message}
      </Flex>
    );
  };

  const [loadingTable, setLoadingTable] = useState(true);
  useEffect(() => {
    setLoadingTable(false);
  }, []);

  return (
    <>
      <Flex justifyContent="center" pb="10px">
        <Flex flexDir="column" w={{ base: "98%" }} mt={{ base: "16px", xl: 0 }}>
          {isSanctioned && <SanctionBanner />}
        </Flex>
      </Flex>
      <Flex
        justifyContent="center"
        p={{ base: "0 16px", xs: 0 }}
        pointerEvents={isSanctioned ? "none" : "auto"}
        opacity={isSanctioned ? 0.5 : 1}
      >
        <Flex
          borderRadius="24px"
          p={{ base: "0", xl: "0 30px" }}
          flexDir="column"
          gap={{ base: "24px", md: "20px" }}
          w={{
            base: "100%",
            md: "calc(100vw - 32px)",
            xl: "calc(100vw - 300px)",
          }}
          mt={{ base: "16px", xl: "0px" }}
        >
          <Header />
          <Flex
            gap={{ base: 2, md: 4 }}
            p={{ base: "0 8px", lg: 0 }}
            width={"100%"}
            flexWrap={"nowrap"}
          >
            <CategoryDropdown
              options={tags}
              selectedOption={selectedTag}
              onSelect={(option: string) => {
                filterChain(option);
              }}
              isCheckbox={true}
              placeholder={"Networks"}
            />
            <CategoryDropdown
              options={activities}
              selectedOption={selectedActivity}
              onSelect={(option: string) => {
                filterActivity(option);
              }}
              isCheckbox={true}
              placeholder={"Activity"}
            />
            <CategoryDropdown
              options={assets}
              selectedOption={selectedAsset}
              onSelect={(option: string) => {
                filterAsset(option);
              }}
              isCheckbox={true}
              placeholder={"Assets"}
            />
          </Flex>

          <FeaturedDefi featuredData={featuredDefi} />

          {loadingTable && (
            <Flex
              justifyContent={"center"}
              alignItems={"center"}
              height="60vh"
              ml={{ base: 0, xl: "-100px" }}
            >
              <Center>
                <Spinner size="lg" />
              </Center>
            </Flex>
          )}

          {selectedActivity.length > 0 &&
            selectedAsset.length > 0 &&
            !loadingTable &&
            Object.keys(filteredSections).map((section) => (
              <Fragment key={section}>
                <Box
                  overflowX="auto"
                  maxW="100%"
                  // width={{
                  //   base: "100%",
                  //   md: "900px",
                  //   xl: "calc(100vw - 310px)",
                  // }}
                >
                  {getFilteredRows(sections[section]).length > 0 && (
                    <>
                      <Flex
                        alignItems={"flex-start"}
                        gap={2}
                        mb={2}
                        p={{ base: "0px 12px", lg: 0 }}
                      >
                        <Text
                          fontSize={{ base: "16px" }}
                          fontWeight="600"
                          color="textPrimary"
                          marginBottom={{
                            base: 0,
                          }}
                        >
                          {section}
                        </Text>
                      </Flex>
                      <DefiList rows={getFilteredRows(sections[section])} />
                    </>
                  )}
                </Box>
              </Fragment>
            ))}
        </Flex>
      </Flex>
      {renderMissingSelectionMessage()}
    </>
  );
};

export default Defi;
